import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { CONTROL_SURFACE } from "../../lib/craft";
import { cn } from "../../lib/utils";

/* Press feedback is direct response: the fastest, smallest motion the policy names, with no
 * overshoot — so it is a CSS transform on the tactile cadence, not a spring. The Button is
 * in every first paint, and this is the whole of its motion; the spring-pressed variants
 * beUI ships (beui.dev/components/motion/button) live behind lazy boundaries as
 * `StatefulButton`. */
const BUTTON_GEOMETRY = {
  default: "h-7 rounded-md px-2.5 py-1 text-xs",
  sm: "h-6 rounded-md px-2 py-0.5 text-xs [&_svg:not([data-chart])]:size-3.5",
  lg: "h-8 rounded-md px-3 py-1.5 text-sm",
  icon: "h-7 w-7 rounded-md p-0 text-xs",
  iconSm: "h-6 w-6 rounded-md p-0 text-xs [&_svg:not([data-chart])]:size-3.5",
  // `touch` is the default geometry: it grows to the 44px target on a coarse pointer and
  // stays compact under a mouse, so asking for touch never inflates a desktop control.
  touch: "h-7 rounded-md px-2.5 py-1 text-xs [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:px-4 [@media(pointer:coarse)]:text-sm",
  touchIcon: "h-7 w-7 rounded-md p-0 text-xs [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11",
  multiline:
    "min-h-7 max-w-full justify-start whitespace-normal break-words rounded-md px-2 py-1 text-left text-supporting [@media(pointer:coarse)]:min-h-11",
  // One rail row: a single line at the reading size, its icon filling the line.
  nav: "relative h-7 w-full justify-start gap-2 rounded-md px-1.5 text-left text-body font-normal [&_svg:not([data-chart])]:size-[1.125rem] [@media(pointer:coarse)]:h-11 data-[active=true]:before:absolute data-[active=true]:before:-left-2 data-[active=true]:before:inset-y-[7px] data-[active=true]:before:w-0.5 data-[active=true]:before:rounded-full data-[active=true]:before:bg-brand",
  // The rail's project control: a mark beside a name and its organization, two lines.
  railCard:
    "h-[3.25rem] w-full justify-start gap-2.5 rounded-lg p-2 text-left text-body",
  adaptiveIcon:
    "h-7 w-7 rounded-md p-0 text-xs [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11",
} as const;

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-tactile ease-tactile active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg:not([data-chart])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // The page's one main action: filled orange (BRAND §6). Every other action is ink
        // (`solid`), an outline or a ghost.
        default:
          "border border-action-edge bg-gradient-to-b from-action-top to-action font-semibold text-action-foreground shadow-[0_6px_24px_-8px_hsl(var(--action-top)/0.7)] hover:brightness-105 active:brightness-95",
        // Ink, for the one place a filled action must not be orange.
        solid:
          "border border-foreground bg-gradient-to-b from-foreground/80 to-foreground text-background shadow-control hover:from-foreground/70 hover:shadow-control-hover active:shadow-control-pressed",
        destructive:
          "border border-destructive bg-gradient-to-b from-destructive/80 to-destructive font-semibold text-destructive-foreground shadow-[0_6px_20px_-8px_hsl(var(--destructive)/0.6)] hover:brightness-105 active:brightness-95",
        success:
          "border border-success bg-gradient-to-b from-success/80 to-success font-semibold text-success-foreground shadow-[0_6px_20px_-8px_hsl(var(--success)/0.55)] hover:brightness-105 active:brightness-95",
        // Every action beside the main one: the same lit material, quieter.
        outline: `${CONTROL_SURFACE} text-foreground [&_svg]:text-muted-foreground hover:[&_svg]:text-foreground`,
        secondary:
          "border border-transparent bg-secondary text-secondary-foreground shadow-control hover:border-border hover:bg-accent hover:shadow-control-hover active:shadow-control-pressed [&_svg]:text-muted-foreground hover:[&_svg]:text-foreground",
        ghost:
          "text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent/70",
        link: "text-primary underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        default: BUTTON_GEOMETRY.default,
        sm: BUTTON_GEOMETRY.sm,
        lg: BUTTON_GEOMETRY.lg,
        icon: BUTTON_GEOMETRY.icon,
        iconSm: BUTTON_GEOMETRY.iconSm,
        touch: BUTTON_GEOMETRY.touch,
        touchIcon: BUTTON_GEOMETRY.touchIcon,
        multiline: BUTTON_GEOMETRY.multiline,
        nav: BUTTON_GEOMETRY.nav,
        railCard: BUTTON_GEOMETRY.railCard,
        adaptiveIcon: BUTTON_GEOMETRY.adaptiveIcon,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const geometry = BUTTON_GEOMETRY[size ?? "default"];
    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          geometry,
          "max-w-full [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11",
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
